import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const API = '/api';

const PERIODOS = [
  { label: '7 días',  dias: 7 },
  { label: '30 días', dias: 30 },
  { label: '90 días', dias: 90 },
];

export default function Uptime() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(30);
  const [filterCop, setFilterCop] = useState('');

  useEffect(() => {
    fetch(`${API}/transactions`)
      .then(r => r.json())
      .then(data => { setTxs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Calcular uptime por cargador en el período
  const calcularUptime = () => {
    const ahora = new Date();
    const desde = new Date(ahora.getTime() - periodo * 24 * 60 * 60 * 1000);

    // Filtrar sesiones del período
    const enPeriodo = txs.filter(t => {
      const st = new Date(t.start_time || t.startTime);
      return st >= desde && st <= ahora;
    });

    // Agrupar por cargador
    const porCargador = {};
    enPeriodo.forEach(t => {
      const cpId = t.cp_id || t.cpId;
      const cop = t.cop || 'Blanco y Negro';
      if (filterCop && cop !== filterCop) return;
      if (!porCargador[cpId]) porCargador[cpId] = { cpId, cop, diasConActividad: new Set(), sesiones: 0, energia: 0 };
      const dia = (t.start_time || t.startTime)?.slice(0, 10);
      if (dia) porCargador[cpId].diasConActividad.add(dia);
      porCargador[cpId].sesiones++;
      porCargador[cpId].energia += t.energy_delivered || t.energyDelivered || 0;
    });

    return Object.values(porCargador).map(c => {
      const diasActivo = c.diasConActividad.size;
      const uptime = Math.round((diasActivo / periodo) * 100);
      return {
        ...c,
        diasActivo,
        uptime,
        estado: uptime >= 90 ? 'excelente' : uptime >= 70 ? 'bueno' : uptime >= 50 ? 'regular' : 'critico'
      };
    }).sort((a, b) => a.uptime - b.uptime);
  };

  // Tendencia diaria de actividad
  const calcularTendencia = () => {
    const ahora = new Date();
    const dias = [];
    for (let i = Math.min(periodo, 30) - 1; i >= 0; i--) {
      const d = new Date(ahora.getTime() - i * 24 * 60 * 60 * 1000);
      const dStr = d.toISOString().slice(0, 10);
      const activos = new Set(txs
        .filter(t => (t.start_time||t.startTime)?.startsWith(dStr))
        .map(t => t.cp_id || t.cpId)
      ).size;
      dias.push({
        dia: `${d.getDate()}/${d.getMonth()+1}`,
        cargadores: activos,
      });
    }
    return dias;
  };

  const estadoColor = e => ({ excelente:'#4ade80', bueno:'#60a5fa', regular:'#f59e0b', critico:'#f87171' })[e] || '#888';
  const estadoLabel = e => ({ excelente:'Excelente', bueno:'Bueno', regular:'Regular', critico:'Crítico' })[e] || e;
  const pad = n => String(n).padStart(2,'0');

  if (loading) return <div style={{padding:'3rem',textAlign:'center',color:'#666'}}>Cargando uptime...</div>;

  const stats = calcularUptime();
  const tendencia = calcularTendencia();
  const avgUptime = stats.length > 0 ? Math.round(stats.reduce((a,c)=>a+c.uptime,0)/stats.length) : 0;
  const criticos = stats.filter(c=>c.estado==='critico').length;
  const totalSesiones = stats.reduce((a,c)=>a+c.sesiones,0);
  const totalEnergia = stats.reduce((a,c)=>a+c.energia,0);

  // Datos para gráfica por COP
  const porCop = ['Blanco y Negro'].map(cop => ({
    cop,
    uptime: Math.round(stats.filter(c=>c.cop===cop).reduce((a,c)=>a+c.uptime,0)/Math.max(stats.filter(c=>c.cop===cop).length,1))
  })).filter(c=>c.uptime>0);

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem',flexWrap:'wrap',gap:'12px'}}>
        <div className="page-title" style={{margin:0}}>Uptime y disponibilidad</div>
        <div style={{display:'flex',gap:'6px'}}>
          {PERIODOS.map(p => (
            <button key={p.dias} onClick={()=>setPeriodo(p.dias)}
              style={{padding:'6px 14px',border:'1px solid',borderRadius:'6px',cursor:'pointer',fontSize:'13px',
                background:periodo===p.dias?'#2a2d3a':'#1a1d27',
                borderColor:periodo===p.dias?'#4ade80':'#2a2d3a',
                color:periodo===p.dias?'#4ade80':'#666'}}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="metrics">
        <div className="metric-card">
          <div className="metric-label">Uptime promedio</div>
          <div className="metric-value" style={{color:avgUptime>=90?'#4ade80':avgUptime>=70?'#60a5fa':'#f59e0b'}}>{avgUptime}%</div>
          <div className="metric-sub">últimos {periodo} días</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cargadores críticos</div>
          <div className="metric-value" style={{color:criticos>0?'#f87171':'#4ade80'}}>{criticos}</div>
          <div className="metric-sub">uptime bajo 50%</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Sesiones en período</div>
          <div className="metric-value">{totalSesiones.toLocaleString()}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Energía entregada</div>
          <div className="metric-value">{(totalEnergia/1000).toFixed(1)} MWh</div>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <div className="card-title">Cargadores activos por día — últimos {Math.min(periodo,30)} días</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={tendencia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="dia" tick={{fill:'#666',fontSize:10}} interval={Math.floor(tendencia.length/6)} />
              <YAxis tick={{fill:'#666',fontSize:11}} />
              <Tooltip contentStyle={{background:'#1a1d27',border:'1px solid #2a2d3a',color:'#fff'}} />
              <Line type="monotone" dataKey="cargadores" stroke="#4ade80" strokeWidth={2} dot={false} name="Cargadores activos" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div className="card-title">Uptime promedio por COP</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={porCop} barSize={60}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="cop" tick={{fill:'#aaa',fontSize:13}} />
              <YAxis domain={[0,100]} tick={{fill:'#666',fontSize:11}} unit="%" />
              <Tooltip contentStyle={{background:'#1a1d27',border:'1px solid #2a2d3a',color:'#fff'}} formatter={v=>`${v}%`} />
              <Bar dataKey="uptime" fill="#60a5fa" radius={[6,6,0,0]} name="Uptime" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="filters">
        <select value={filterCop} onChange={e=>setFilterCop(e.target.value)}>
          <option value="">Todos los COP</option>
          <option value="Blanco y Negro">Blanco y Negro</option>
        </select>
        <span style={{fontSize:'12px',color:'#555',marginLeft:'auto'}}>{stats.length} cargadores analizados</span>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Cargador</th>
              <th>COP</th>
              <th>Uptime</th>
              <th style={{width:'160px'}}>Disponibilidad</th>
              <th>Días activo</th>
              <th>Sesiones</th>
              <th>Energía</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {stats.map(c => (
              <tr key={c.cpId}>
                <td><span className="badge">{c.cpId}</span></td>
                <td style={{color:'#aaa'}}>{c.cop}</td>
                <td style={{fontWeight:'600',color:estadoColor(c.estado)}}>{c.uptime}%</td>
                <td>
                  <div className="soc-bar-bg">
                    <div className="soc-bar-fill" style={{width:`${c.uptime}%`,background:estadoColor(c.estado)}}></div>
                  </div>
                </td>
                <td style={{color:'#aaa'}}>{c.diasActivo} / {periodo}</td>
                <td>{c.sesiones}</td>
                <td style={{fontWeight:'500'}}>{c.energia.toLocaleString()} kWh</td>
                <td>
                  <span style={{background:`${estadoColor(c.estado)}22`,color:estadoColor(c.estado),padding:'2px 8px',borderRadius:'5px',fontSize:'11px',fontWeight:'500'}}>
                    {estadoLabel(c.estado)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
